import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { ContractService } from '../../service/base/contract';
import { ApproveContractCommandHandler } from '../../service/command/approve-contact-command-handler.service';
import { ContractFormCommandHandlerResolver } from '../../service/command/contract-form-command-handler-resolver.service';
import { PrintContractCommandHandler } from '../../service/command/print-contract-command-handler.service';
import { PrintContractTerminationFormCommandHandler } from '../../service/command/print-contract-termination-form-command-handler';
import { ShowLeaseAgreementInvoicesCommandHandler } from '../../service/command/show-lease-agreement-invoices-command-handler';
import { TerminateContractCommandHandler } from '../../service/command/terminate-contract-command-handler.service';
import { WithdrawApprovalCommandHandler } from '../../service/command/withdraw-approval-command-handler.service';
import { WithdrawTerminationCommandHandler } from '../../service/command/withdraw-termination-command-handler.service';
import { ContractFormComponentBase } from '../base/contract-form.base';
import { DwellingContract } from './../../model/dwelling-contract';
import { DwellingContractService } from './../../service/data/dwelling-contract.service';
import { ActivatedRoute } from '@angular/router';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { DwellingInspection } from '../../../dwelling-inspection/model/dwelling-inspection';
import { SubleaseRequest } from '../../model/sublease-request';
import { standardCommand, Command } from '../../../core/model/command';
import { Fragment } from '../../../core/model/fragment';
import { VoidContractCommandHandler } from '../../service/command/void-contract-command-handler.service';
import { ESignLeaseAgreementContractCommandHandler } from '../../service/command/esign-lease-agreement-document-command-handler.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { ESignKeyRecieptCommandHandler } from '../../service/command/esign-key-reciept-document-command-handler.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { LeaseAgreementLogService } from '../../service/data/lease-agreement-log.service';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { ContactService } from '../../service/data/contact.service';
import { LeaseAgreementImdConsumptionService } from '../../service/data/lease-agreement-imd-consumption.service';
import { Tenant } from '../../model/tenant';
import { CompanyService } from '../../service/data/company.service';
import { Company } from '../../model/company';
import { signal } from '@angular/core';
import { LanguageService } from '../../../core/service/language.service';

const formControlNames = {
  nameplate: 'nameplate',
  allowEntranceWithExtraKey: 'allowEntranceWithExtraKey',
  atticStorageNumber: 'atticStorageNumber',
  basementStorageNumber: 'basementStorageNumber',
  otherStorageNumber: 'otherStorageNumber',
  parkingIncludedInRent: 'parkingIncludedInRent',
  specialTerms: 'specialTerms',
  negotiatingClause: 'negotiatingClause',
  organizationOfTenants: 'organizationOfTenants',
  otherOrganizationOfTenants: 'otherOrganizationOfTenants',
};

@Component({
    selector: 'app-contract-form',
    templateUrl: './dwelling-contract-form.component.html',
    styleUrls: ['./dwelling-contract-form.component.scss'],
    providers: [
        ApproveContractCommandHandler,
        ContractFormCommandHandlerResolver,
        DeleteFormCommandHandler,
        PrintContractTerminationFormCommandHandler,
        TerminateContractCommandHandler,
        VoidContractCommandHandler,
        ShowLeaseAgreementInvoicesCommandHandler,
        WithdrawApprovalCommandHandler,
        WithdrawTerminationCommandHandler,
        ESignLeaseAgreementContractCommandHandler,
        ESignKeyRecieptCommandHandler,
        PrintContractCommandHandler,
        CompanyService,
        { provide: ContractService, useExisting: DwellingContractService },
        { provide: DataService, useExisting: DwellingContractService },
        { provide: CompanyService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DwellingContractFormComponent extends ContractFormComponentBase<DwellingContract> {
  openDocumentsAccordion = false;
  constructor(
    _route: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    dataService: DwellingContractService,
    cdf: ColumnDefinitionFactory,
    cds: ColumnDefinitionService,
    private columnFactory: ColumnDefinitionFactory,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    userAccount: UserAccountService,
    public companyService: CompanyService,
    handlerResolver: ContractFormCommandHandlerResolver,
    messageService: MessageService,
    messageDispatcher: ComponentMessageDispatcher,
    navigation: NavigationService,
    logService: LeaseAgreementLogService,
    public dwellingContractService: DwellingContractService,
    public contactDataService: ContactService,
    leaseAgreementImdConsumptionService: LeaseAgreementImdConsumptionService,
    private readonly _languageService: LanguageService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(
      messageDispatcher,
      cdf,
      fb,
      fcf,
      userAccount,
      cds,
      _route,
      dataService,
      messageService,
      navigation,
      commandDispatcher,
      handlerResolver,
      logService,
      leaseAgreementImdConsumptionService,
      _languageService
    );

    this.AddSubscription(
      _route.fragment.subscribe(fragment => {
        this.openDocumentsAccordion = fragment === this.documentsFragment;
      })
    );

    this.AddSubscription(
      userAccount.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures)) {
            this.addMenuItemDefinition('COMMON.COMMAND.ESIGN_KEY_RECIEPT', leaseAgreementCommand.esignKeyReciept);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT', leaseAgreementCommand.print);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.VOID', leaseAgreementCommand.void);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );

    this.dwellingInspectionColumnDefinition = [
      this.columnFactory.createTranslation('COMMON.STATUS', 'statusCaption'),
      this.columnFactory.create('DWELLING_INSPECTION.NUMBER', 'number'),
      this.columnFactory.create('DWELLING_INSPECTION.INSPECTION_TYPE', 'dwellingInspectionType'),
      this.columnFactory.createDate('DWELLING_INSPECTION.INSPECTION_DATE', 'inspectionDate'),
      this.columnFactory.create('DWELLING_INSPECTION.INSPECTOR', 'inspector'),
      this.columnFactory.create('COMMON.LEASE_AGREEMENT_NUMBER', 'movingOutTenantLeaseAgreementNumber'),
      this.columnFactory.create('COMMON.MOVING_OUT_TENANT_NAME', 'movingOutTenantOneName'),
      this.columnFactory.createDate('COMMON.MOVING_OUT_DATE', 'tenantMovingOutDate'),
      this.columnFactory.create('COMMON.MOVING_OUT_TENANT_PHONE', 'movingOutTenantOnePhone', { visibility: ColumnVisibility.optional }),
      this.columnFactory.create('COMMON.MOVING_OUT_TENANT_EMAIL', 'movingOutTenantOneEmail', { visibility: ColumnVisibility.optional }),
    ];
  }

  tenant1Name = signal<string>('');
  tenant2Name = signal<string>('');
  companies: Company[];
  subleaseRequests: SubleaseRequest[];
  dwellingInspectionColumnDefinition: ColumnDefinition[];
  companyId = '';
  hasNameplate: boolean;
  leaseAgreementDocumentCount = 0;

  linkDocument() {
    const cmd = new Command<DwellingContract>(leaseAgreementCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  onDocumentsCountChanged(count: number) {
    this.leaseAgreementDocumentCount = count;
    this._cdr.markForCheck();
  }

  get activeSubleaseRequestsCount(): number {
    if (!this.subleaseRequests || this.subleaseRequests.length === 0) {
      return 0;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return this.subleaseRequests.filter(request => {
      if (!request.fromDate || !request.toDate) {
        return false;
      }
      const fromDate = new Date(request.fromDate.toString());
      const toDate = new Date(request.toDate.toString());
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);

      return (fromDate <= today && today <= toDate) || fromDate > today;
    }).length;
  }

  get hasSecurity(): boolean {
    const hasDeposit =
      this.dataContext?.depositAmount?.amount > 0 ||
      (this.dataContext?.depositComment && this.dataContext.depositComment.trim().length > 0);

    const hasGuarantor = this.dataContext?.securities && this.dataContext.securities.length > 0;

    return hasDeposit || hasGuarantor;
  }

  private _rentalObjectShort: string;
  get titleContextInfo(): string {
    return this.dataContext?.id
      ? `${this.dataContext.leaseAgreementNumber}, ${this._rentalObjectShort}, ${this._languageService.instant(this.dataContext.statusCaption)}`
      : null;
  }

  protected init(data: DwellingContract) {
    super.init(data);
    this.hasNameplate = data.nameplate != null;

    this.companyId = data.company;
    this.tenant1Name.set(this.dataContext.tenantOneName || '');
    this.tenant2Name.set(this.dataContext.tenantTwoName || '');

    this.subleaseRequests = data.subleaseRequests;

    const rentalObjectShort = this.dataContext.rentalObject.match(/[A-Za-z].*/);
    this._rentalObjectShort = rentalObjectShort ? rentalObjectShort[0] : '';

    // this.form.controls['nameplate'].valueChanges.subscribe(x => {
    //   if (x == '') {
    //     this.hasNameplate = false;
    //     this.updateNameplate();
    //   }
    // });
  }

  updateNameplate() {
    if (this.hasNameplate) return;

    let hasTenant = this.tenant1Name() != '';
    let hasCompany = !(this.companyId == '' || this.companyId == undefined || this.companyId == null);

    if (hasCompany && hasTenant) {
      this.dwellingContractService.getNameplate(this.companyId, this.tenant1Name(), this.tenant2Name()).subscribe(x => {
        this.form.controls['nameplate'].setValue(x);
      });
    }
  }

  onTenant1Change(tenant: Tenant) {
    super.onTenantOneChange(tenant);
    this.tenant1Name.set(tenant?.name ?? '');

    if (this.tenant1Name() == '') this.form.controls['nameplate'].setValue('');

    this.hasNameplate = false;
    this.updateNameplate();
  }
  onTenant2Change(tenant: Tenant) {
    this.tenant2Name.set(tenant?.name ?? '');
    this.hasNameplate = false;
    this.updateNameplate();
  }

  protected getFormGroup(): FormGroup {
    const numberLength = 30;
    let group = super.getFormGroup();
    group.addControl(formControlNames.nameplate, this.fcf.create('NAMEPLATE.LABEL', this.dataContext.nameplate));
    group.addControl(
      formControlNames.allowEntranceWithExtraKey,
      this.fcf.create('DWELLING_LEASE_AGREEMENT_FORM.ALLOW_ENTRANCE_WITH_EXTRA_KEY_LABEL', this.dataContext.allowEntranceWithExtraKey)
    );
    group.addControl(
      formControlNames.atticStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.ATTIC_STORAGE_NUMBER_LABEL',
        this.dataContext.atticStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.basementStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.BASEMENT_STORAGE_NUMBER_LABEL',
        this.dataContext.basementStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.otherStorageNumber,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.OTHER_STORAGE_NUMBER_LABEL',
        this.dataContext.otherStorageNumber,
        numberLength
      )
    );
    group.addControl(
      formControlNames.parkingIncludedInRent,
      this.fcf.createMaxLength(
        'DWELLING_LEASE_AGREEMENT_FORM.PARKING_INCLUDED_IN_RENT_LABEL',
        this.dataContext.parkingIncludedInRent,
        numberLength,
        'LEASE_AGREEMENT.HELP_TEXT.PARKINGINCLUDED'
      )
    );
    group.addControl('subleaseRequests', this.fb.group({}));
    group.addControl(
      formControlNames.negotiatingClause,
      this.fcf.create('NEGOTIATION_FORM.NEGOTIATION_CLAUSE_LABEL', this.dataContext.negotiatingClause)
    );
    group.addControl(
      formControlNames.organizationOfTenants,
      this.fcf.create('NEGOTIATION_FORM.ORGANIZATION_OF_TENANTS_LABEL', this.dataContext.organizationOfTenants)
    );
    group.addControl(
      formControlNames.otherOrganizationOfTenants,
      this.fcf.create('NEGOTIATION_FORM.OTHER_ORGANIZATION_OF_TENANTS_LABEL', this.dataContext.otherOrganizationOfTenants)
    );

    if (this.isNew && !group.controls['usage'].value) group.controls['usage'].setValue('Lägenheten uthyres för att användas till bostad.');
    return group;
  }

  onInspectionRowClicked(e: { context: DwellingInspection; event: any }): void {
    this.navigation.navigate(['dwelling-inspection', 'inspection', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  resetNameplateIfnecessary() {
    if (this.form.controls['nameplate'].value != '') return;

    this.hasNameplate = false;
    this.updateNameplate();
  }

  onRentalObjectChanged(rentalObject: VacantRentalObject) {
    super.onRentalObjectChanged(rentalObject);
    if (rentalObject && rentalObject.id) {
      this.companyId = rentalObject.company;
      this.updateNameplate();
      this.form.controls['negotiatingClause'].setValue(rentalObject.hasNegotiationProcedure);
      this.dataContext.negotiatingClause = rentalObject.hasNegotiationProcedure;
      this.form.controls['organizationOfTenants'].setValue(rentalObject.organizationOfTenants);
      this.dataContext.organizationOfTenants = rentalObject.organizationOfTenants;
      this.form.controls['otherOrganizationOfTenants'].setValue(rentalObject.otherOrganizationOfTenants);
      this.dataContext.otherOrganizationOfTenants = rentalObject.otherOrganizationOfTenants;

      this.dataContext.atticStorageNumber = rentalObject.atticStorageNumber;
      this.form.controls[formControlNames.atticStorageNumber].setValue(rentalObject.atticStorageNumber);
      this.dataContext.basementStorageNumber = rentalObject.basementStorageNumber;
      this.form.controls[formControlNames.basementStorageNumber].setValue(rentalObject.basementStorageNumber);
      this.dataContext.otherStorageNumber = rentalObject.otherStorageNumber;
      this.form.controls[formControlNames.otherStorageNumber].setValue(rentalObject.otherStorageNumber);
      this.dataContext.parkingIncludedInRent = rentalObject.parkingIncludedInRent;
      this.form.controls[formControlNames.parkingIncludedInRent].setValue(rentalObject.parkingIncludedInRent);
    }
  }

  protected createModel(): DwellingContract {
    let model = new DwellingContract();
    model.invoiceAddress.country = '';
    return model;
  }

  protected prepareSave(): DwellingContract {
    const model = super.prepareSave();
    model.companyId = this.companyId;
    model.subleaseRequests = this.subleaseRequests;
    model.tenantOneName = this.tenant1Name();
    model.tenantTwoName = this.tenant2Name();
    return model;
  }
}
